import { onScopeDispose, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { useDebounceFn, useEventListener } from '@vueuse/core';
import {
  acceptsReadyEvent,
  PREVIEW_DEBOUNCE_MS,
  PREVIEW_READY_TIMEOUT_MS,
  previewOrigin,
} from '@/domain/design/preview-message';
import type { ISfPreviewMessage } from '@/domain/design/preview-message';
import { Globals } from '@/modules/Globals';

/** What the channel needs of the preview iframe: the window it posts to and hears from. */
export interface IPreviewFrame {
  contentWindow: Pick<Window, 'postMessage'> | null;
}

/**
 * The admin's half of the preview handshake (CON-11, D-ADM-2): waits for the
 * framed storefront's `sf:ready` (from the iframe's window and the storefront
 * origin only), answers every ready with the current draft and posts each
 * draft change (debounced) to the storefront origin, never to `"*"`. Without
 * a draft yet (`message` null: the saved look is still loading) nothing is
 * posted and the store shows its saved look; the draft goes out once it
 * arrives.
 */
export function useDesignPreviewChannel(options: {
  frame: Ref<IPreviewFrame | null>;
  src: Ref<string>;
  message: Ref<ISfPreviewMessage | null>;
}) {
  const origin = previewOrigin(Globals.website);
  const ready = ref(false);
  const failed = ref(false);
  const frameKey = ref(0);
  let timer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  function clearTimer() {
    if (timer) clearTimeout(timer);
    timer = null;
  }

  /** A new document is loading: wait for its `sf:ready`, give up after 15 s. */
  function start() {
    ready.value = false;
    failed.value = false;
    clearTimer();
    timer = setTimeout(() => {
      if (!disposed && !ready.value) failed.value = true;
    }, PREVIEW_READY_TIMEOUT_MS);
  }

  function post() {
    const message = options.message.value;
    if (disposed || !ready.value || !message) return;
    options.frame.value?.contentWindow?.postMessage(message, origin);
  }
  const postSoon = useDebounceFn(post, PREVIEW_DEBOUNCE_MS);

  useEventListener(window, 'message', (event: MessageEvent) => {
    if (
      !acceptsReadyEvent(
        event,
        origin,
        options.frame.value?.contentWindow ?? null
      )
    )
      return;
    ready.value = true;
    failed.value = false;
    clearTimer();
    post(); // every ready gets the current draft at once (a reload inside the frame too)
  });

  watch(options.src, start, { immediate: true }); // a store switch loads another store
  watch(options.message, () => {
    void postSoon(); // each draft or photos change
  });

  /** «Reintentar»: a fresh iframe (its `key` changes) and a new wait. */
  function reload() {
    frameKey.value += 1;
    start();
  }

  onScopeDispose(() => {
    disposed = true;
    clearTimer();
  });

  return { ready, failed, frameKey, reload };
}
